import { SettingOutlined, UserOutlined } from "@ant-design/icons";
import { RiBuildingLine, RiExpandDiagonalLine } from "@remixicon/react";
import { Button, message, Modal, Popover, Spin, Switch, Tooltip } from "antd";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useCIQMutation, useCIQQuerySubscription } from "hooks/ciq-gql-hooks";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useParams } from "react-router";
import { GET_MATERIAL_CHANGELOG_CONFIG } from "services/graphQL/ciq-queries";
import { MUTATION_UPDATE_MATERIAL_CHANGELOG_CONFIG } from "services/graphQL/mutations";
import { DateUtils } from "utils/dateutils";

const COMMENT_TRUNCATE_CHARS = 100;

const SETTINGS_POPOVER_OVERLAY_STYLE = { borderRadius: "6px" };

export type MaterialUpdateItem = {
  id: string;
  material_id: string;
  title: string;
  update_summary: string;
  updated_by: string;
  updated_at: string;
  responsible_contractor: string;
  comment?: string;
};

function MaterialUpdateRow({
  item
}: Readonly<{
  item: MaterialUpdateItem;
}>) {
  const history = useHistory();
  const { projectId } = useParams<{ projectId: string }>();
  const [expanded, setExpanded] = useState(false);
  const [showTitleTooltip, setShowTitleTooltip] = useState(false);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [showContractorTooltip, setShowContractorTooltip] = useState(false);
  const contractorRef = useRef<HTMLSpanElement>(null);

  const openMaterialDetails = () => {
    history.push(`/project/${projectId}/materials/${item.id}`);
  };

  const { needsTruncation, truncatedComment } = useMemo(() => {
    const comment = item.comment ?? "";
    if (comment.length <= COMMENT_TRUNCATE_CHARS) {
      return { needsTruncation: false, truncatedComment: comment };
    }

    const slice = comment.slice(0, COMMENT_TRUNCATE_CHARS);
    const lastSpace = slice.lastIndexOf(" ");
    return {
      needsTruncation: true,
      truncatedComment: slice.slice(
        0,
        lastSpace > 0 ? lastSpace : COMMENT_TRUNCATE_CHARS
      )
    };
  }, [item.comment]);

  return (
    <div className="py-3 flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={openMaterialDetails}
          className="inline-flex items-center space-x-2 text-left bg-transparent p-0 cursor-pointer border-0 min-w-0"
          data-testid={`material-update-row-${item.id}`}
        >
          <span className="text-ciq-primary-900 underline text-sm font-medium flex-shrink-0">
            {item.material_id}
          </span>
          <span className="text-ciq-gray-300 flex-shrink-0">|</span>
          <Tooltip
            title={item.title}
            open={showTitleTooltip}
            onOpenChange={(open) => {
              const el = titleRef.current;
              const overflowing = !!el && el.scrollWidth > el.clientWidth;
              setShowTitleTooltip(open && overflowing);
            }}
          >
            <span
              ref={titleRef}
              className="font-normal text-sm min-w-0 truncate"
            >
              {item.title}
            </span>
          </Tooltip>
        </button>
        <div className="flex items-center space-x-1 text-xs text-black text-opacity-70 ml-2 flex-shrink-0">
          <RiBuildingLine size={14} className="flex-shrink-0" />
          <Tooltip
            title={item.responsible_contractor}
            open={showContractorTooltip}
            onOpenChange={(open) => {
              const el = contractorRef.current;
              const overflowing = !!el && el.scrollWidth > el.clientWidth;
              setShowContractorTooltip(open && overflowing);
            }}
          >
            <span ref={contractorRef} className="max-w-[20ch] truncate">
              {item.responsible_contractor}
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="font-medium text-sm">{item.update_summary}</div>

      {item.comment && (
        <div className="bg-black bg-opacity-[0.04] p-3 rounded text-xs text-black text-opacity-70 break-all">
          {needsTruncation && !expanded ? (
            <>
              {truncatedComment}...{" "}
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="font-bold text-black text-opacity-90 cursor-pointer whitespace-nowrap bg-transparent border-0 p-0 inline align-baseline"
              >
                view more
              </button>
            </>
          ) : (
            <>
              {item.comment}
              {needsTruncation && (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    className="font-bold text-black text-opacity-90 cursor-pointer whitespace-nowrap bg-transparent border-0 p-0 inline align-baseline"
                  >
                    view less
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-center space-x-2 text-xs text-black text-opacity-60">
        <UserOutlined className="!text-ciq-gray-700 hover:!text-ciq-gray-700" />
        <span>{item.updated_by}</span>
        <span>•</span>
        <span>{DateUtils.format(item.updated_at, "MM-DD-YYYY, hh:mm A")}</span>
      </div>
    </div>
  );
}

function MaterialUpdatesList({
  materials
}: Readonly<{
  materials: MaterialUpdateItem[];
}>) {
  return (
    <>
      {materials.map((item, idx) => (
        <div key={item.id}>
          <MaterialUpdateRow item={item} />
          {idx < materials.length - 1 && (
            <span className="block w-full overflow-hidden whitespace-nowrap text-ciq-gray-300 text-xs leading-none tracking-widest">
              {"-".repeat(300)}
            </span>
          )}
        </div>
      ))}
    </>
  );
}

function MaterialUpdatesHeading({
  count,
  dateRange,
  showCount
}: Readonly<{
  count: number;
  dateRange: string;
  showCount: boolean;
}>) {
  return (
    <div className="flex items-center min-w-0">
      <span className="whitespace-nowrap">Material Progress Updates</span>
      {showCount && (
        <span className="ml-2 flex flex-shrink-0 items-center justify-center min-w-[32px] h-[22px] px-2 rounded-full border border-solid border-ciq-gray-300 text-xs font-normal text-black text-opacity-70">
          {count}
        </span>
      )}
      <span className="ml-2 whitespace-nowrap text-xs font-normal text-black text-opacity-60">
        ({dateRange})
      </span>
    </div>
  );
}

function MaterialUpdatesSettings({
  showUpdates,
  onToggle
}: Readonly<{
  showUpdates: boolean;
  onToggle: (checked: boolean) => void;
}>) {
  return (
    <div className="w-72">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Show Updates</span>
        <Switch
          className="material-updates-toggle"
          checked={showUpdates}
          onChange={onToggle}
          data-testid="material-updates-show-toggle"
        />
      </div>
      <div className="mt-1 text-xs text-black text-opacity-60">
        {showUpdates
          ? "Turn off if updates are not needed."
          : "Turn on to see updates in the last 7 days."}
      </div>
    </div>
  );
}

function MaterialUpdatesDashboard({
  materials
}: Readonly<{
  materials: MaterialUpdateItem[] | undefined;
}>) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { projectId } = useParams<{ projectId: string }>();
  const {
    gqlClientForProject,
    tokenContents,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);
  const userId = tokenContents?.claims?.["x-hasura-user-id"];

  const changelogConfigVariables = useMemo(
    () => ({ project_id: projectId, user_id: userId }),
    [projectId, userId]
  );

  const { data: changelogConfigData } = useCIQQuerySubscription<{
    project_users: Array<{
      material_changelog_config: { enable_changelog?: boolean } | null;
    }>;
  }>(GET_MATERIAL_CHANGELOG_CONFIG, {
    client: gqlClientForProject,
    variables: changelogConfigVariables,
    skip: !gqlClientForProject || !projectId || !userId
  });

  const remoteEnableChangelog =
    changelogConfigData?.project_users?.[0]?.material_changelog_config
      ?.enable_changelog ?? false;

  const [showUpdates, setShowUpdates] = useState(remoteEnableChangelog);

  const pendingMutationRef = useRef(false);

  useEffect(() => {
    if (pendingMutationRef.current) return;
    setShowUpdates(remoteEnableChangelog);
  }, [remoteEnableChangelog]);

  const [updateMaterialChangelogConfig] = useCIQMutation(
    MUTATION_UPDATE_MATERIAL_CHANGELOG_CONFIG,
    { client: gqlClientForProject }
  );

  const handleToggle = useCallback(
    async (checked: boolean) => {
      // Archived projects are read-only, so the preference isn't saved there;
      // the switch only shows or hides the panel for this visit.
      if (isProjectArchived) {
        setShowUpdates(checked);
        return;
      }
      pendingMutationRef.current = true;
      setShowUpdates(checked);
      try {
        const response = await updateMaterialChangelogConfig({
          variables: {
            project_id: projectId,
            user_id: userId,
            material_changelog_config: { enable_changelog: checked }
          }
        });
        if (!response.success) {
          setShowUpdates(!checked);
          message.error("Failed to update setting. Please try again.");
        }
      } finally {
        pendingMutationRef.current = false;
      }
    },
    [updateMaterialChangelogConfig, projectId, userId, isProjectArchived]
  );

  const { count, dateRange } = useMemo(() => {
    const today = new Date();
    const sixDaysAgo = new Date(today);
    sixDaysAgo.setDate(today.getDate() - 6);
    return {
      count: materials?.length ?? 0,
      dateRange: `${DateUtils.format(sixDaysAgo, "D MMM")} - ${DateUtils.format(
        today,
        "D MMM"
      )}`
    };
  }, [materials]);

  const materialsCount = materials?.length ?? 0;

  const renderBody = (listWrapperClass: string): ReactNode => {
    if (!materials) {
      return (
        <div className="flex w-full h-40 justify-center items-center">
          <Spin />
        </div>
      );
    }
    if (materials.length === 0) {
      return (
        <div
          className="flex w-full h-40 justify-center items-center text-xs text-black text-opacity-60"
          data-testid="material-updates-empty-on"
        >
          There are no material updates for this period.
        </div>
      );
    }
    return (
      <div className={listWrapperClass}>
        <MaterialUpdatesList materials={materials} />
      </div>
    );
  };

  const settingsContent = useMemo(
    () => (
      <MaterialUpdatesSettings
        showUpdates={showUpdates}
        onToggle={handleToggle}
      />
    ),
    [showUpdates, handleToggle]
  );

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    if (!isSettingsOpen) return undefined;
    const close = () => setIsSettingsOpen(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [isSettingsOpen]);

  const settingsPopover = (
    <Popover
      trigger="click"
      placement="bottomRight"
      styles={{ container: SETTINGS_POPOVER_OVERLAY_STYLE }}
      content={settingsContent}
      open={isSettingsOpen}
      onOpenChange={setIsSettingsOpen}
    >
      <Button
        type="text"
        size="small"
        className="!bg-transparent !shadow-none !border-0 hover:!bg-transparent focus:!bg-transparent"
        icon={<SettingOutlined className="text-ciq-primary-900" />}
        data-testid="material-updates-settings-button"
      />
    </Popover>
  );

  return (
    <>
      <div className="box-border w-full">
        <div className="box-header bottom-border !p-6 !justify-between">
          <MaterialUpdatesHeading
            count={count}
            dateRange={dateRange}
            showCount={showUpdates}
          />
          <div className="flex items-center">
            {settingsPopover}
            {showUpdates && (
              <Button
                type="text"
                size="small"
                onClick={() => setIsModalOpen(true)}
                disabled={materialsCount === 0}
                className="!bg-transparent !shadow-none !border-0 hover:!bg-transparent focus:!bg-transparent !pt-[4px] !pl=[4px]"
                icon={
                  <RiExpandDiagonalLine
                    size={16}
                    className="text-ciq-primary-900"
                  />
                }
                data-testid="material-updates-expand-button"
              />
            )}
          </div>
        </div>

        {showUpdates &&
          renderBody("max-h-[500px] overflow-y-auto px-6 py-2 w-full")}
      </div>

      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={720}
        title={
          <div className="font-semibold text-base pr-8">
            <MaterialUpdatesHeading
              count={count}
              dateRange={dateRange}
              showCount={showUpdates}
            />
          </div>
        }
        data-testid="material-updates-modal"
      >
        {showUpdates && renderBody("max-h-[70vh] overflow-y-auto px-2")}
      </Modal>
    </>
  );
}

export default MaterialUpdatesDashboard;
